import { Inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { Router } from '@angular/router'
import { tap } from 'rxjs/operators'
import { SignupDto, User } from '../models'
import { TokenService } from './token.service'
import { ProfileService } from './profile.service'
import { AccountType } from '../../../generated/DTM/graphql'
import { BaseLogActionService } from './graphql/log-action/base-log-action.service'
import { SBS_ENVIRONMENT } from '../../sbs-common.module'
import { SbsEnvironment } from '../models/sbs-environment'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  AUTH_API = ''
  TWO_FACTOR_API = ''

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenService: TokenService,
    private profileService: ProfileService,
    private baseLogActionService: BaseLogActionService,
    @Inject(SBS_ENVIRONMENT) private env: SbsEnvironment,
  ) {
    this.AUTH_API = `${this.env.baseURL}/auth`
    this.TWO_FACTOR_API = `${this.env.baseURL}/2fa`
  }

  login(loginName: string, password: string): Observable<any> {
    return this.http
      .post(`${this.AUTH_API}/signin`, {
        loginName,
        password,
      })
      .pipe(
        tap((tokens: any) => {
          this.tokenService.saveAccessToken(tokens.accessToken)
          this.tokenService.saveRefreshToken(tokens.refreshToken)
          const user = this.tokenService.getUser()
        }),
      )
  }

  loginWithQrCode(code: string): Observable<any> {
    return this.http
      .post(`${this.AUTH_API}/signin-with-qr`, {
        code,
      })
      .pipe(
        tap((tokens: any) => {
          this.tokenService.saveAccessToken(tokens.accessToken)
          this.tokenService.saveRefreshToken(tokens.refreshToken)
          const user = this.tokenService.getUser()
          this.baseLogActionService.logLogin(user?.sub ?? '')
        }),
      )
  }

  generateQR(): Observable<any> {
    return this.http.post(
      `${this.TWO_FACTOR_API}/generate-qr`,
      {},
      {
        responseType: 'blob',
        headers: {
          ignoreInterceptor: 'true',
        },
      },
    )
  }

  generateLoginQr(): Observable<any> {
    return this.http.post(`${this.AUTH_API}/login-qr`, {}, { responseType: 'blob' })
  }

  turnOnQr(code: string): Observable<any> {
    return this.http.post(
      `${this.TWO_FACTOR_API}/turn-on-qr`,
      {
        code,
      },
      {
        headers: {
          ignoreInterceptor: 'true',
        },
      },
    )
  }

  verify2FA(code: string): Observable<any> {
    return this.http
      .post(
        `${this.TWO_FACTOR_API}/authenticate`,
        {
          code,
        },
        {
          headers: {
            ignoreInterceptor: 'true',
          },
        },
      )
      .pipe(
        tap((tokens: any) => {
          this.tokenService.saveAccessToken(tokens.accessToken)
          this.tokenService.saveRefreshToken(tokens.refreshToken)
          const user = this.tokenService.getUser()
          this.baseLogActionService.logLogin(user?.sub ?? '')
        }),
      )
  }

  signup(signupDto: SignupDto): Observable<any> {
    return this.http.post(`${this.AUTH_API}/signup`, signupDto).pipe(
      tap((tokens: any) => {
        this.tokenService.saveAccessToken(tokens.accessToken)
        this.tokenService.saveRefreshToken(tokens.refreshToken)
      }),
    )
  }

  refreshToken(token?: string) {
    return this.http
      .post<any>(`${this.AUTH_API}/refresh`, null, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })
      .pipe(
        tap((tokens: any) => {
          this.tokenService.saveAccessToken(tokens.accessToken)
          this.tokenService.saveRefreshToken(tokens.refreshToken)
        }),
      )
  }

  logout(refreshToken?: string, deviceId?: string) {
    return this.http
      .post(
        `${this.AUTH_API}/logout`,
        {
          refreshToken,
          deviceId,
        },
        {
          headers: {
            ignoreInterceptor: 'true',
          },
        },
      )
      .pipe(
        tap(() => {
          this.profileService.clearProfile()
        }),
      )
  }

  getPathAndState(user: User) {
    const authState = this.getAuthStateFromUser(user)
    const routeToNavigateTo = this.getRouteToNavigateTo(authState)
    return { authState, route: routeToNavigateTo.route }
  }

  //calculates the level of authentication the user has access to.
  getAuthStateFromUser(user: User | null): AuthState {
    if (!user) {
      return AuthState.NOTHING
    }
    if (!user.acceptedLegalTerms) {
      return AuthState.LEGAL_TERMS_NOT_ACCEPTED
    }

    if (user.roles === AccountType.Administrator || user.roles === AccountType.ProjectMember) {
      if (user.isTwoFactorEnabled && user.isTwoFactorAuthenticated) {
        return AuthState.CORRECT_USER_PASS_AND_2FA
      }
      if (user.isTwoFactorEnabled && !user.isTwoFactorAuthenticated) {
        if (user.shouldChangePassword) {
          return AuthState.CORRECT_USER_PASS_ENABLED_2FA_NEEDS_PASSWORD_CHANGE
        } else {
          return AuthState.CORRECT_USER_PASS_CHANGE_AND_ENABLED_2FA
        }
      }
      if (!user.isTwoFactorEnabled && !user.isTwoFactorAuthenticated) {
        if (!user.shouldChangePassword) {
          return AuthState.CORRECT_USER_PASS_AND_CHANGE
        } else {
          return AuthState.CORRECT_USER_PASS
        }
      }
    }

    if (user.roles === AccountType.Participant) {
      //better naming is needed
      if (user.shouldChangePassword) {
        return AuthState.CORRECT_USER_PASS_ENABLED_2FA_NEEDS_PASSWORD_CHANGE
      } else {
        return AuthState.CORRECT_USER_PASS_AND_2FA
      }
    }

    return AuthState.NOTHING
  }

  getRouteToNavigateTo(authState: number) {
    //given the level, navigates to the correct path
    switch (authState) {
      case AuthState.NOTHING:
        return { route: '/login' }
      case AuthState.LEGAL_TERMS_NOT_ACCEPTED:
        return { route: '/login/accept-legal-terms' }
      case AuthState.CORRECT_USER_PASS:
        return { route: '/login/change-password' }
      case AuthState.CORRECT_USER_PASS_AND_CHANGE:
        return { route: '/login/enable-two-factor' }
      case AuthState.CORRECT_USER_PASS_CHANGE_AND_ENABLED_2FA:
        return { route: '/login/two-factor' }
      case AuthState.CORRECT_USER_PASS_ENABLED_2FA_NEEDS_PASSWORD_CHANGE:
        return { route: '/login/change-password' }
      case AuthState.CORRECT_USER_PASS_AND_2FA:
        return { route: '' }
    }
    return { route: '/login' }
  }

  getUser() {
    return this.tokenService.getUser()
  }

  navigateUserToCorrectPath(user: User | null, isInitialNavigation = false) {
    const authState = this.getAuthStateFromUser(user)
    const routeToNavigateTo = this.getRouteToNavigateTo(authState)
    if (isInitialNavigation && routeToNavigateTo.route === '') {
      //fixes refresh when already logged in - in this case, trying to navigate to a /login step when already logged in fails.
      return
    }
    return this.router.navigateByUrl(routeToNavigateTo.route, {
      state: { authState: authState },
    })
  }
}

export enum AuthState {
  NOTHING,
  LEGAL_TERMS_NOT_ACCEPTED,
  CORRECT_USER_PASS,
  CORRECT_USER_PASS_AND_CHANGE,
  CORRECT_USER_PASS_CHANGE_AND_ENABLED_2FA,
  CORRECT_USER_PASS_ENABLED_2FA_NEEDS_PASSWORD_CHANGE,
  CORRECT_USER_PASS_AND_2FA,
}
